import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  ArrowRight,
  CheckCircle, 
  TrendingUp, 
  Clock, 
  Mail, 
  Bell,
  Target,
  Zap,
  Award,
  Users,
  Lightbulb,
  AlertCircle,
  Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";

export default function MaximizeEarningsPage() {
  const tips = [
    {
      category: "Email Setup",
      icon: Mail,
      color: "from-blue-500 to-cyan-500",
      items: [
        {
          title: "Register Multiple Email Addresses",
          description: "You can register up to 10 email addresses. More emails = more task opportunities = more earnings!",
          impact: "High"
        },
        {
          title: "Use Reliable Email Providers",
          description: "Gmail and Outlook have the best deliverability. Avoid temporary or less-known email services.",
          impact: "Medium"
        },
        {
          title: "Check Spam Folders Regularly",
          description: "Sometimes task emails land in spam. Check daily and mark GigsEasy emails as 'Not Spam'.",
          impact: "High"
        },
        {
          title: "Keep Email Notifications On",
          description: "Enable push notifications for your email app so you never miss a time-sensitive task.",
          impact: "Medium"
        }
      ]
    },
    {
      category: "Task Completion",
      icon: Target,
      color: "from-[#00B894] to-emerald-500",
      items: [
        {
          title: "Complete Tasks Quickly",
          description: "Many high-paying tasks have limited spots. The faster you act, the more tasks you'll secure.",
          impact: "High"
        },
        {
          title: "Read Instructions Carefully",
          description: "Some tasks have specific requirements. Missing a step can mean no credit for your work.",
          impact: "High"
        },
        {
          title: "Maintain Quality",
          description: "Quality matters more than quantity. Rushed or incomplete tasks may be rejected.",
          impact: "Medium"
        },
        {
          title: "Track Your Completion Rate",
          description: "A high completion rate can unlock access to premium, higher-paying tasks.",
          impact: "Medium"
        }
      ]
    },
    {
      category: "Time Management",
      icon: Clock,
      color: "from-purple-500 to-pink-500",
      items: [
        {
          title: "Set Dedicated Task Times",
          description: "Check for new tasks 3-4 times daily: morning, lunch, evening, and before bed.",
          impact: "High"
        },
        {
          title: "Peak Hours Strategy",
          description: "New tasks are often released between 9 AM - 12 PM EST. Be online during these hours.",
          impact: "Medium"
        },
        {
          title: "Batch Similar Tasks",
          description: "Complete similar task types together for faster processing and better efficiency.",
          impact: "Low"
        },
        {
          title: "Use Mobile for Quick Tasks",
          description: "Simple click tasks can be done from your phone while commuting or waiting.",
          impact: "Medium"
        }
      ]
    },
    {
      category: "Profile Optimization",
      icon: Users,
      color: "from-orange-500 to-red-500",
      items: [
        {
          title: "Complete Your Profile 100%",
          description: "Full profiles get priority access to tasks. Add all requested information.",
          impact: "High"
        },
        {
          title: "Verify All Your Emails",
          description: "Unverified emails don't receive tasks. Verify immediately after adding.",
          impact: "High"
        },
        {
          title: "Keep Information Updated",
          description: "Update your payment details and contact info to avoid payout delays.",
          impact: "Medium"
        },
        {
          title: "Build Your Reputation",
          description: "Consistent, quality work builds your reputation score, unlocking better opportunities.",
          impact: "Medium"
        }
      ]
    }
  ];

  const mistakes = [
    {
      mistake: "Ignoring task emails",
      consequence: "Missing out on high-paying opportunities that fill up fast",
      solution: "Set up email notifications and check regularly"
    },
    {
      mistake: "Using only one email",
      consequence: "Limiting your earning potential by 90%",
      solution: "Register all 10 allowed email addresses"
    },
    {
      mistake: "Rushing through tasks",
      consequence: "Failed verifications and rejected submissions",
      solution: "Take 5 extra seconds to ensure completion"
    },
    {
      mistake: "Waiting too long to withdraw",
      consequence: "No benefit from holding points",
      solution: "Withdraw regularly once you hit minimum"
    }
  ];

  const proTips = [
    {
      icon: Zap,
      title: "The 10-Email Strategy",
      description: "Create 10 Gmail accounts specifically for GigsEasy. Use a naming pattern like yourname.gigs1@gmail.com for easy organization."
    },
    {
      icon: Bell,
      title: "Notification Hack",
      description: "Set up a dedicated email filter that plays a unique sound for GigsEasy emails. You'll never miss a task again."
    },
    {
      icon: Award,
      title: "Consistency Wins",
      description: "Completing tasks every day, even just a few, keeps you in 'active' status which prioritizes you for new task distribution."
    },
    {
      icon: Lightbulb,
      title: "Smart Scheduling",
      description: "Use your calendar to set 3 daily reminders to check for tasks. Top earners check at 9 AM, 1 PM, and 7 PM."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <Link to="/register">
            <Button className="bg-[#00B894] hover:bg-[#00A085] text-white">
              Start Earning Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-white via-[#00B894]/5 to-[#0984E3]/5">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#00B894]/10 text-[#00B894] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <TrendingUp className="w-4 h-4" />
            Pro tips from top earners
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Maximize Your{" "}
            <span className="bg-gradient-to-r from-[#00B894] to-[#0984E3] bg-clip-text text-transparent">
              Earnings
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Learn the strategies that top Gigsters use to earn $500+ per month. 
            These proven tips can double or triple your income!
          </p>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 px-6 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl font-bold text-[#00B894]">10x</p>
              <p className="text-slate-400 text-sm">More tasks with 10 emails</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#00B894]">3x</p>
              <p className="text-slate-400 text-sm">Earnings with quick response</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#00B894]">95%</p>
              <p className="text-slate-400 text-sm">Completion rate target</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#00B894]">$500+</p>
              <p className="text-slate-400 text-sm">Top earner monthly average</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Tips Sections */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto space-y-16">
          {tips.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              <div className="flex items-center gap-4 mb-8">
                <div className={`w-14 h-14 bg-gradient-to-br ${section.color} rounded-2xl flex items-center justify-center`}>
                  <section.icon className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">{section.category}</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {section.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="bg-slate-50 rounded-2xl p-6 hover:bg-slate-100 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        item.impact === 'High' ? 'bg-[#00B894]/10 text-[#00B894]' :
                        item.impact === 'Medium' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-200 text-slate-600'
                      }`}>
                        {item.impact} Impact
                      </span>
                    </div>
                    <p className="text-slate-600">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pro Tips Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Pro Tips from Top Earners
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              These insider strategies are used by our highest-earning members
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {proTips.map((tip, index) => (
              <div key={index} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6">
                <div className="w-12 h-12 bg-[#00B894]/20 rounded-xl flex items-center justify-center mb-4">
                  <tip.icon className="w-6 h-6 text-[#00B894]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{tip.title}</h3>
                <p className="text-slate-400">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Avoid These Common Mistakes
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Don't leave money on the table. Here's what NOT to do:
            </p>
          </div>
          <div className="space-y-4">
            {mistakes.map((item, index) => (
              <div key={index} className="bg-red-50 border border-red-100 rounded-2xl p-6">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-3 md:w-1/3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <span className="font-semibold text-red-800">{item.mistake}</span>
                  </div>
                  <div className="md:w-1/3 text-red-600 text-sm">
                    {item.consequence}
                  </div>
                  <div className="md:w-1/3 flex items-center gap-2 text-green-700 bg-green-50 px-4 py-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">{item.solution}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Star className="w-16 h-16 text-[#00B894] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Ready to Become a Top Earner?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Apply these strategies and watch your earnings grow. 
            The top 10% of Gigsters earn 5x more than average!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-[#00B894] hover:bg-[#00A085] text-white px-8">
                Start Earning Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/how-to-earn">
              <Button size="lg" variant="outline" className="border-slate-300">
                Read Getting Started Guide
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
